import { Alert } from '@/experiences/sales-portal/domain/insights/blocs/types/alert';
import { partnerSupplier } from '@/utils/environment';
import { transformAlertCards } from '@/experiences/sales-portal/domain/insights/blocs/transformers/alert-transformer';
import { useMemo } from 'react';
import { useSalesPortalGetHiddenAlertsQuery } from '@/experiences/sales-portal/domain/insights/blocs/documents/hidden-alerts.generated';

/** Result of {@link useHiddenAlertsLoader}. */
export interface UseHiddenAlertsLoaderResult {
  alerts: Alert[];
  error: Error | null;
  loading: boolean;
  refetch: () => void;
}

/**
 * Loads the partner organization's HIDDEN hub alerts for the Insights ("For
 * You") feed and maps the raw `card_data_json` payload to domain {@link Alert}s.
 *
 * @returns {@link UseHiddenAlertsLoaderResult}
 *
 * @example
 * const { alerts, loading } = useHiddenAlertsLoader();
 */
export const useHiddenAlertsLoader = (): UseHiddenAlertsLoaderResult => {
  const { data, loading, error, refetch } = useSalesPortalGetHiddenAlertsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
    },
  });

  const alerts = useMemo(
    () => transformAlertCards(data?.getAllHubAlerts.card_data_json ?? []),
    [data],
  );

  return {
    alerts,
    error: error ?? null,
    loading: loading && !data,
    refetch: () => {
      void refetch();
    },
  };
};
